import { inlineNodes, inlinePlain } from "../../cms/rich-inline.js";
/**
 * Runtime binding -- how a published override reaches the page.
 *
 * PAWTENANT-SEO-EDITOR-V3-NOCODE-WORKSPACE-001 (TEST origin)
 * PAWTENANT-CONTENT-STUDIO-INTEGRATION-001 (LIVE port)
 *
 * useSeoPageContent(map) returns accessors the page renders through:
 *
 *   c.text("hero.heading")        the override when one is published, else the
 *                                 map's compiled default
 *   c.items("faq.items")          the collection with overrides applied
 *   c.image("hero.image")         { src, alt } with an uploaded image resolved
 *   c.variant("hero.alignment")   an approved variant value
 *   c.visible("key_change_alert") section visibility
 *
 * FALLBACK IS THE DEFAULT, NOT AN ERROR PATH
 * A missing override, a malformed override or an override for a field that no
 * longer exists all resolve to the compiled default. Visitors never see a
 * blank, and a page with no override renders byte-identically to before.
 *
 * WHERE THE OVERRIDES COME FROM (the LIVE difference)
 * On TEST this read Supabase (seo_published_revisions) at runtime. On LIVE the
 * source is the Content Studio bundle frozen into src/generated/cmsBundle.json
 * by scripts/cms/fetch.mjs before `vite build` (src/cms/bundle.ts). It is a
 * plain import, so the build-time SSR and the browser resolve the same values
 * with no network and no loading state.
 */

import { Fragment, type ReactNode } from "react";
import { cmsOverrides } from "@/cms/bundle";
import type { CmsImageOverride, CmsOverrideValue } from "@/cms/types";
import {
  VISIBILITY_FIELD_SUFFIX,
  collectionById,
  fieldById,
  type SeoPageContentMap,
} from "./types";

export type SeoContentOverrides = Record<string, CmsOverrideValue>;

export interface SeoResolvedImage {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  /** True when the image is an uploaded override rather than the compiled asset. */
  overridden: boolean;
}

export interface SeoPageContent {
  routePath: string;
  text: (fieldId: string) => string;
  items: (collectionId: string) => Array<Record<string, string>>;
  image: (imageFieldId: string, altFieldId?: string) => SeoResolvedImage;
  variant: (fieldId: string) => string;
  visible: (sectionId: string) => boolean;
  /** Which fields carry a published override -- for data attributes and QA. */
  overridden: (id: string) => boolean;
  /** The FAQ items of a collection as { q, a } pairs, for both the accordion and the schema. */
  faqs: (collectionId: string) => Array<{ q: string; a: string }>;
}

function isImageOverride(v: unknown): v is CmsImageOverride {
  return !!v && typeof v === "object" && !Array.isArray(v) && typeof (v as CmsImageOverride).src === "string" && /^(\/(?!\/)|https:\/\/)/.test((v as CmsImageOverride).src);
}

/**
 * Pure resolver, shared by the hook, the SSR prerender and the build guard.
 * Everything defensive lives here so the page component stays declarative.
 */
export function resolvePageContent(map: SeoPageContentMap, overrides: SeoContentOverrides | null | undefined): SeoPageContent {
  const o: SeoContentOverrides = overrides && typeof overrides === "object" ? overrides : {};

  const text = (fieldId: string): string => {
    const def = fieldById(map, fieldId);
    if (!def) return "";
    const v = o[fieldId];
    if (def.editable && typeof v === "string" && v.trim()) return v;
    return def.value;
  };

  const items = (collectionId: string): Array<Record<string, string>> => {
    const def = collectionById(map, collectionId);
    if (!def) return [];
    const v = o[collectionId];
    if (!Array.isArray(v) || v.length === 0) return def.items;
    const managedCols = def.columns.filter((c) => c.managed).map((c) => c.key);
    if (def.keyed) {
      // Cards: the override decides ORDER and editable text; managed columns
      // (icons, tones) always come from the compiled item with the same key.
      const byKey = new Map(def.items.map((i) => [i.key, i]));
      const out: Array<Record<string, string>> = [];
      const seen = new Set<string>();
      for (const raw of v) {
        const key = typeof raw?.key === "string" ? raw.key : "";
        const base = byKey.get(key);
        if (!base || seen.has(key)) continue;
        seen.add(key);
        const merged: Record<string, string> = { ...base };
        for (const c of def.columns) {
          if (c.managed || c.key === "key") continue;
          const val = raw[c.key];
          if (typeof val === "string" && val.trim()) merged[c.key] = val;
        }
        out.push(merged);
      }
      // A malformed override that dropped cards falls back whole rather than
      // silently shortening a designed grid.
      return out.length === def.items.length ? out : def.items;
    }
    const out = v
      .filter((raw) => raw && typeof raw === "object")
      .map((raw) => {
        const item: Record<string, string> = {};
        for (const c of def.columns) {
          const val = raw[c.key];
          item[c.key] = typeof val === "string" ? val : "";
        }
        return item;
      })
      .filter((item) => def.columns.filter((c) => !c.managed && !managedCols.includes(c.key)).every((c) => item[c.key].trim().length > 0));
    return out.length >= Math.max(1, def.minItems) ? out : def.items;
  };

  const image = (imageFieldId: string, altFieldId?: string): SeoResolvedImage => {
    const def = fieldById(map, imageFieldId);
    const altDef = altFieldId ? fieldById(map, altFieldId) : null;
    const altOverridden = !!altDef && altDef.editable && typeof o[altFieldId!] === "string" && (o[altFieldId!] as string).trim().length > 0;
    const alt = altFieldId ? text(altFieldId) : "";
    if (!def) return { src: "", alt, overridden: false };
    const v = o[imageFieldId];
    if (def.editable && isImageOverride(v)) {
      return {
        src: v.src,
        // The studio stores the alt with the image; a separate alt field
        // override still wins because it is the more specific edit.
        alt: altOverridden ? alt : (typeof v.alt === "string" && v.alt.trim() ? v.alt : alt),
        width: typeof v.width === "number" ? v.width : undefined,
        height: typeof v.height === "number" ? v.height : undefined,
        overridden: true,
      };
    }
    return { src: def.value, alt, overridden: false };
  };

  const variant = (fieldId: string): string => {
    const def = fieldById(map, fieldId);
    if (!def) return "";
    const allowed = def.constraints?.allowedValues ?? [];
    const v = o[fieldId];
    if (def.editable && typeof v === "string" && allowed.includes(v)) return v;
    return def.value;
  };

  const visible = (sectionId: string): boolean => variant(`${sectionId}${VISIBILITY_FIELD_SUFFIX}`) !== "hidden";

  const overridden = (id: string): boolean => {
    const v = o[id];
    if (v === undefined || v === null) return false;
    const fdef = fieldById(map, id);
    if (fdef) {
      if (!fdef.editable) return false;
      if (fdef.kind === "image") return isImageOverride(v);
      if (fdef.kind === "variant" || fdef.kind === "toggle") return typeof v === "string" && (fdef.constraints?.allowedValues ?? []).includes(v);
      return typeof v === "string" && v.trim().length > 0;
    }
    const cdef = collectionById(map, id);
    return !!cdef && Array.isArray(v) && v.length > 0;
  };

  const faqs = (collectionId: string) => items(collectionId).map((i) => ({ q: i.question ?? "", a: i.answer ?? "" }));

  return { routePath: map.routePath, text, items, image, variant, visible, overridden, faqs };
}

/** The page-side hook: the frozen bundle's overrides for this route, else the compiled defaults. */
export function useSeoPageContent(map: SeoPageContentMap): SeoPageContent {
  return resolvePageContent(map, cmsOverrides(map.routePath));
}

/**
 * Inline emphasis for paragraph fields: `**text**` renders as <strong>. This is
 * the ONLY markup a paragraph may carry; it is not HTML, so nothing an editor
 * types can become a tag, a handler or a link.
 */
export function renderInline(text: string): ReactNode { return inlineNodes(text); }
/** Plain text for metadata and structured data. */
export function stripInline(text: string): string { return inlinePlain(text); }
